import type { LanguageData } from "./types";

export const languageData: LanguageData = {
  slug: "team-dev",
  name: "チーム開発",
  nameEn: "Team Development",
  seo: {
    title:
      "チーム開発を学べる女性向けプログラミングスクール | ゆめさくエンジニア",
    description:
      "Git/GitHub運用・コードレビュー・アジャイル/スクラム・ペアプログラミングまで、チーム開発の実践スキルをゆめさくエンジニアで学びましょう。採用で最も重視されるポイントです。",
    url: "/columns/team-dev-programming-school",
  },
  hero: {
    title: "チーム開発を学べる女性向けプログラミングスクール",
    englishTitle: "Learn Team Development",
  },
  breadcrumb: [
    { name: "ホーム", url: "/" },
    { name: "プログラミング言語・技術別", url: "/columns/category/lang" },
    { name: "チーム開発" },
  ],
  overview: {
    heading: "チーム開発とは？",
    paragraphs: [
      "チーム開発とは、複数のエンジニアが協力してひとつのソフトウェアを開発するプロセスと、そのために必要なスキル・手法の総称です。Git/GitHubによるバージョン管理、コードレビュー、アジャイル/スクラムなどの開発手法が含まれます。",
      "実際の開発現場では、一人でコードを書く時間よりも、チームでコミュニケーションを取りながら開発を進める時間の方が圧倒的に多くなります。優れたコードを書く力だけでなく、チームとして成果を出すための協働スキルが求められます。",
      "プログラミングスクールで個人開発は学べても、チーム開発を実践的に経験できる機会は限られています。採用面接では「チーム開発の経験」が最も重視されるポイントの一つです。",
    ],
    useCases: [
      {
        label: "Git/GitHubフロー",
        description:
          "ブランチ戦略、プルリクエスト、マージ、コンフリクト解消など、チーム開発に不可欠なGit/GitHubの実践的なワークフローを習得します。",
      },
      {
        label: "コードレビュー",
        description:
          "他のメンバーのコードをレビューし、品質向上と知識共有を図る文化を実践します。レビューの書き方・受け方の両方を学びます。",
      },
      {
        label: "アジャイル/スクラム",
        description:
          "スプリント計画、デイリースクラム、振り返りなど、アジャイル開発の手法を実際のプロジェクトで体験します。",
      },
      {
        label: "ペアプログラミング・モブプログラミング",
        description:
          "2人以上で一緒にコードを書く手法を通じて、コミュニケーション力と問題解決力を高めます。",
      },
    ],
  },
  whyLearn: {
    heading: "チーム開発を学ぶべき3つの理由",
    items: [
      {
        title: "採用面接で最も重視される経験",
        description:
          "多くの企業が未経験者に求めるのは「チームで開発した経験」です。個人開発だけでは得られない、プルリクエスト・コードレビュー・タスク管理の経験は、採用での大きなアドバンテージになります。",
      },
      {
        title: "実務で即戦力になれる",
        description:
          "入社後すぐにGitHub上でプルリクエストを出し、コードレビューを受け、チームの開発フローに参加できるスキルがあれば、即戦力として高い評価を得られます。",
      },
      {
        title: "コミュニケーション力が自然と身につく",
        description:
          "チーム開発を通じて、技術的な議論の仕方、質問の仕方、ドキュメントの書き方など、エンジニアとして必要なコミュニケーション力が自然と磨かれます。",
      },
    ],
  },
  luagateCurriculum: {
    heading: "ゆめさくエンジニアでチーム開発を学ぶ",
    paragraphs: [
      "ゆめさくエンジニアでは、Phase 6の実践フェーズでチーム開発を本格的に経験します。他の受講生と実際にチームを組み、Git/GitHubを使った開発フローの中でコードレビュー・タスク管理・スクラムイベントを実践します。",
      "一人では得られない「チームで開発する感覚」を体験し、実務で即座に活かせるチーム開発スキルを身につけます。",
    ],
    topics: [
      "Git/GitHubによるチーム開発フロー",
      "ブランチ戦略（Git Flow・GitHub Flow）",
      "プルリクエストとコードレビューの実践",
      "アジャイル/スクラム開発の基礎と実践",
      "タスク管理（GitHub Issues・プロジェクトボード）",
      "ペアプログラミング・モブプログラミング",
      "実践プロジェクト：チームでのWebアプリケーション開発",
    ],
  },
  careerPaths: {
    heading: "チーム開発スキルを活かせるキャリア",
    items: [
      {
        title: "全エンジニア職で必須",
        description:
          "フロントエンド・バックエンド・インフラ・モバイルなど、あらゆるエンジニア職でチーム開発は日常的に行われます。チーム開発経験は採用で最も重視されるポイントのひとつであり、入社後の立ち上がりの速さにも直結します。",
      },
      {
        title: "スクラムマスター",
        description:
          "アジャイル/スクラム開発のファシリテーターとして、チームの生産性向上と自律的な改善を支援する専門職です。",
        salary: "500〜800万円",
      },
      {
        title: "テックリード",
        description:
          "技術面でチームをリードし、コードレビュー・アーキテクチャ設計・技術的意思決定を主導する役割です。チーム開発の経験が豊富であることが前提条件です。",
        salary: "600〜1,000万円",
      },
    ],
  },
  comparison: {
    heading: "独学 vs ゆめさくエンジニア",
    items: [
      {
        label: "学習期間",
        selfStudy: "独学ではチーム開発の経験を積む機会がほぼない",
        luagate: "Phase 6で実際のチーム開発を2〜3ヶ月間体験",
      },
      {
        label: "挫折率",
        selfStudy: "一人でGitを学んでも実践イメージが湧かず挫折",
        luagate: "チームメンバーと協力しながら実践的に習得",
      },
      {
        label: "実践プロジェクト",
        selfStudy: "個人開発の経験しか積めない",
        luagate: "実際のチームでPR・レビュー・スクラムを経験",
      },
      {
        label: "質問対応",
        selfStudy: "コンフリクト解消やレビュー方法がわからない",
        luagate: "メンターがチーム開発の実践をリアルタイムでサポート",
      },
      {
        label: "就職サポート",
        selfStudy: "「チーム開発経験なし」が採用のボトルネックに",
        luagate: "チーム開発の実績をポートフォリオとしてアピール可能",
      },
    ],
  },
  faq: [
    {
      question: "プログラミング初心者でもチーム開発に参加できますか？",
      answer:
        "ゆめさくエンジニアではPhase 1〜5で個人のプログラミングスキルをしっかり身につけた後にPhase 6のチーム開発に進むため、十分なスキルを持った状態で参加できます。初心者の方でも安心してください。",
    },
    {
      question: "チームのメンバーはどのように決まりますか？",
      answer:
        "ゆめさくエンジニアでは学習進度やスキルレベルを考慮してチーム編成を行います。同じ時期に学んでいる仲間とチームを組むため、レベル感が近く協力しやすい環境で学べます。",
    },
    {
      question: "チーム開発の経験は転職でどれくらい評価されますか？",
      answer:
        "非常に高く評価されます。多くの採用担当者が「個人開発の経験よりもチーム開発の経験を重視する」と回答しています。Git/GitHubの操作・コードレビュー・アジャイル開発の実践経験は、未経験者の中で大きな差別化ポイントになります。",
    },
    {
      question: "リモートでもチーム開発はできますか？",
      answer:
        "はい、ゆめさくエンジニアのチーム開発はオンラインで実施します。実際の企業でもリモートでのチーム開発が一般的であり、Slack・GitHub・ビデオ会議を使った非同期・同期コミュニケーションのスキルも実践的に身につきます。",
    },
  ],
  relatedLanguages: [
    { slug: "git", name: "Git" },
    { slug: "system-design", name: "システム設計" },
    { slug: "upstream", name: "上流工程" },
    { slug: "docker", name: "Docker" },
  ],
};
